import React, { FC, useMemo, useState } from 'react';
import type { StylesConfig } from 'react-select';
import Select from 'react-select';

import styled from 'styled-components';

// theme
import aampeTheme from '../../../constants/aampeTheme';

//hooks
import useOutsideClickDetector from '../../../hooks/useOutsideClickDetector';

interface StyledProps {
    isDisabled?: boolean;
}
interface Option {
    value: number;
    label: string;
}
interface AampeTimePickerProps {
    selectedDate: Date | null;
    setSelectedDate: (value: Date) => void;
    isDisabled?: boolean;
}

const customSelectStyles: StylesConfig = {
    control: () => ({
        display: 'none',
    }),
    dropdownIndicator: () => ({
        display: 'none',
    }),
    menu: (provided) => ({
        ...provided,
        borderRight: 'none',
        width: '60px',
        bottom: '30px',
        fontSize: '13px',
        lineHeight: '14px',
        boxShadow: 'none',
        border: `1px solid ${aampeTheme.colors.backgroundWG01}`,
    }),
    option: (provided) => ({
        ...provided,
        width: '100%',
        textAlign: 'center',
        cursor: 'pointer',
    }),
    menuList: (provided) => ({
        ...provided,
        '&::-webkit-scrollbar': {
            display: 'none',
            background: 'transparent',
            width: 0,
        },
        scrollbarWidth: 'none',
    }),
};

const AampeTimePicker: FC<React.PropsWithChildren<AampeTimePickerProps>> = ({
    selectedDate,
    isDisabled = false,
    setSelectedDate,
}) => {
    const [showTimePicker, setShowTimePicker] = useState<boolean>(false);

    const menuRef = useOutsideClickDetector(() => setShowTimePicker(false));

    const renderHourOptions: Option[] = useMemo(
        () =>
            Array.from({ length: 24 }, (_, i) => ({
                value: i,
                label: i.toString().padStart(2, '0'),
            })),
        [],
    );

    const renderMinuteOptions: Option[] = useMemo(
        () =>
            Array.from({ length: 60 }, (_, i) => ({
                value: i,
                label: i.toString().padStart(2, '0'),
            })),
        [],
    );

    const displayValue = useMemo(
        () =>
            selectedDate &&
            `${selectedDate
                .getHours()
                .toString()
                .padStart(2, '0')} : ${selectedDate
                .getMinutes()
                .toString()
                .padStart(2, '0')}`,
        [selectedDate],
    );

    return (
        <div ref={menuRef}>
            <TimePickerValue
                isDisabled={isDisabled}
                onClick={() =>
                    !isDisabled && setShowTimePicker(!showTimePicker)
                }
            >
                {displayValue}
            </TimePickerValue>
            {showTimePicker && (
                <TimePicker>
                    <Select
                        isDisabled={isDisabled}
                        menuIsOpen
                        menuPlacement="top"
                        value={{
                            value: selectedDate?.getHours(),
                            label: selectedDate?.getHours(),
                        }}
                        onChange={(e) => {
                            const newTime = new Date(selectedDate as Date);
                            newTime.setHours((e as Option).value);
                            setSelectedDate(newTime);
                        }}
                        options={renderHourOptions}
                        styles={customSelectStyles}
                    />

                    <Select
                        isDisabled={isDisabled}
                        menuPlacement="top"
                        menuIsOpen
                        value={{
                            value: selectedDate?.getMinutes(),
                            label: selectedDate?.getMinutes(),
                        }}
                        onChange={(e) => {
                            const newTime = new Date(selectedDate as Date);
                            newTime.setMinutes((e as Option).value);
                            setSelectedDate(newTime);
                        }}
                        options={renderMinuteOptions}
                        styles={customSelectStyles}
                    />
                </TimePicker>
            )}
        </div>
    );
};

const TimePickerValue = styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== 'isDisabled',
})<StyledProps>(({ isDisabled }) => ({
    padding: aampeTheme.spacing(0.5, 1.5),
    backgroundColor: isDisabled
        ? aampeTheme.colors.backgroundWG02
        : aampeTheme.colors.white,
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    fontSize: '16px',
    lineHeight: '24px',
    fontWeight: 400,
    minWidth: '250px',
    minHeight: '32px',
    color: aampeTheme.colors.neutral09,
    borderRadius: isDisabled ? aampeTheme.borderRadius.default : '',
}));

const TimePicker = styled.div({
    display: 'flex',
    gap: aampeTheme.spacing(7),
});

export default AampeTimePicker;
